import {Button, Dropdown, DropdownDivider, DropdownItem, Separator} from 'platform/components';
import {Box, Hide, HStack, Show, Space} from 'platform/foundation';

import {ReactElement, useState} from 'react';

import {isNotNil} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {queryParams} from '@dms/shared';

import {Nullish, suffixTestId, TestIdProps, useQueryState} from 'shared/web';

import type {EagGridMaximization, GridDataChange} from 'features/eag-grid';

import {JOB_ITEMS_GRID_CODE} from '../../../constants/jobItemsGridCode';
import {useWorkItemsBulkActions} from '../../../hooks/useWorkItemsBulkActions';
import {useWorkItemsBulkSelection} from '../../../hooks/useWorkItemsBulkSelection';
import {JobGridBand} from './JobGridBand';
import {JobItemsDataGrid} from './JobItemsDataGrid';
import {JobSegmentActionsRow} from './JobSegmentActionsRow';

interface JobWorkItemsSectionProps extends TestIdProps {
  maximization?: EagGridMaximization;
  /** The job segment control; shares one row with the work-item actions. */
  segment: ReactElement | Nullish;
  /** False on the other segments: the section then renders only the segment row. */
  isGridVisible: boolean;
  serviceCaseId: string;
  orderId: string | Nullish;
  jobId: string | Nullish;
  jobName: string | Nullish;
  isCarRental: boolean | Nullish;
  isServicePackageLocked: boolean;
  canAddLabour: boolean;
  canAddMaterial: boolean;
  isReadOnly: boolean | Nullish;
  authorizationProfileId: string | Nullish;
}

/**
 * The work-items block of a job card: the segment row with the work-item actions
 * (bulk work-item actions + selection, add labour/material) and the job-items grid in a
 * white band across the card. It stays mounted on every segment, so the segment keeps
 * its place in the tree and its keyboard focus. The maximized grid repeats the row above
 * itself, because the overlay covers the original one. Owns the grid-data state fed by
 * onGridDataChange so the grid's fetch cycles re-render only this section, not the
 * whole Job card (T20-128140 / T20-138492).
 */
export function JobWorkItemsSection(props: JobWorkItemsSectionProps) {
  const [addLabourJobId, setAddLabourJobId] = useQueryState(
    queryParams.SERVICE_CASE_ADD_LABOUR_JOB_ID
  );
  const [addMaterialRequestJobId, setAddMaterialRequestJobId] = useQueryState(
    queryParams.SERVICE_CASE_ADD_MATERIAL_REQUEST_JOB_ID
  );
  const [gridData, setGridData] = useState<GridDataChange | null>(null);
  const workItemsSelection = useWorkItemsBulkSelection(JOB_ITEMS_GRID_CODE, gridData);
  const workItemsActions = useWorkItemsBulkActions({
    gridData,
    serviceCaseId: props.serviceCaseId,
    serviceOrderId: props.orderId,
    serviceJobId: props.jobId,
    'data-testid': suffixTestId('workItemsBulkActions', props),
  });

  // Grid data of an unmounted grid is stale; the grid reports it again when it mounts.
  if (!props.isGridVisible && isNotNil(gridData)) {
    setGridData(null);
  }

  const canAddAnyItem = props.canAddLabour || props.canAddMaterial;
  const areActionsVisible =
    props.isGridVisible &&
    !props.isReadOnly &&
    !props.isCarRental &&
    (workItemsSelection.isAvailable || canAddAnyItem);

  const handleOpenAddLabour = () => {
    if (props.jobId) {
      setAddLabourJobId(props.jobId);
    }
  };

  const handleOpenAddMaterialRequest = () => {
    if (props.jobId) {
      setAddMaterialRequestJobId(props.jobId);
    }
  };

  const hasRow = isNotNil(props.segment) || areActionsVisible;

  const renderActions = (isCompact: boolean) => (
    <Show when={areActionsVisible}>
      {/* The bulk controls are deliberately NOT gated by the add-item permissions:
          their applicability is backend row-driven (row.actions), and the identical
          per-row actions stay reachable without ADD_LABOUR/ADD_MATERIAL. */}
      <HStack align="center" spacing={2}>
        <Show when={workItemsSelection.isAvailable}>
          <Dropdown
            data-testid={suffixTestId('workItemsMoreActions', props)}
            dropdownControl={
              <Button
                variant="ghostLink"
                leftIcon="navigation/more_vert"
                data-testid={suffixTestId('workItemsMoreActionsButton', props)}
                title={i18n.t('entity.orderRequest.actions.moreActions')}
              />
            }
          >
            <Show when={isCompact}>
              <DropdownItem
                label={`${i18n.t('entity.orderRequest.actions.selectLabour')} (${workItemsSelection.labourCount})`}
                onClick={workItemsSelection.selectLabour}
                isDisabled={workItemsSelection.isLoading || workItemsSelection.labourCount === 0}
                data-testid={suffixTestId('selectLabour', props)}
              />
              <DropdownItem
                label={`${i18n.t('entity.orderRequest.actions.selectMaterial')} (${workItemsSelection.materialCount})`}
                onClick={workItemsSelection.selectMaterial}
                isDisabled={workItemsSelection.isLoading || workItemsSelection.materialCount === 0}
                data-testid={suffixTestId('selectMaterial', props)}
              />
              <DropdownDivider />
            </Show>
            <DropdownItem
              leftIcon="custom/warehouse"
              label={`${i18n.t('entity.orderRequest.actions.issueMaterial')} (${workItemsActions.issueCount})`}
              onClick={workItemsActions.issueMaterial}
              isDisabled={workItemsActions.issueCount === 0}
              data-testid={suffixTestId('issueMaterial', props)}
            />
            <DropdownItem
              leftIcon="action/shopping_cart"
              label={`${i18n.t('entity.orderRequest.actions.requestMaterial')} (${workItemsActions.requestCount})`}
              onClick={workItemsActions.requestMaterial}
              isLoading={workItemsActions.isRequestMaterialPending}
              isDisabled={
                workItemsActions.isRequestMaterialPending || workItemsActions.requestCount === 0
              }
              closeOnClick={false}
              data-testid={suffixTestId('requestMaterial', props)}
            />
            <DropdownItem
              leftIcon="navigation/cancel"
              label={`${i18n.t('entity.orderRequest.actions.requestCancellation')} (${workItemsActions.cancellationCount})`}
              onClick={workItemsActions.requestCancellation}
              isLoading={workItemsActions.isRequestCancellationPending}
              isDisabled={
                workItemsActions.isRequestCancellationPending ||
                workItemsActions.cancellationCount === 0
              }
              closeOnClick={false}
              data-testid={suffixTestId('requestCancellation', props)}
            />
          </Dropdown>
          <Hide when={isCompact}>
            <Button
              variant="ghostLink"
              onClick={workItemsSelection.selectLabour}
              isDisabled={workItemsSelection.isLoading || workItemsSelection.labourCount === 0}
              data-testid={suffixTestId('selectLabour', props)}
              title={`${i18n.t('entity.orderRequest.actions.selectLabour')} (${workItemsSelection.labourCount})`}
            />
            <Button
              variant="ghostLink"
              onClick={workItemsSelection.selectMaterial}
              isDisabled={workItemsSelection.isLoading || workItemsSelection.materialCount === 0}
              data-testid={suffixTestId('selectMaterial', props)}
              title={`${i18n.t('entity.orderRequest.actions.selectMaterial')} (${workItemsSelection.materialCount})`}
            />
          </Hide>
        </Show>
        <Show when={canAddAnyItem}>
          <Button
            variant="ghostLink"
            leftIcon="content/add_circle"
            onClick={handleOpenAddLabour}
            data-testid={suffixTestId('addWork', props)}
            isDisabled={props.isServicePackageLocked || !props.canAddLabour}
            title={i18n.t('entity.addWork.actions.addWork')}
          />
          <Button
            variant="ghostLink"
            leftIcon="content/add_circle"
            onClick={handleOpenAddMaterialRequest}
            data-testid={suffixTestId('addMaterial', props)}
            isDisabled={props.isServicePackageLocked || !props.canAddMaterial}
            title={i18n.t('entity.addMaterial.actions.addMaterial')}
          />
        </Show>
      </HStack>
    </Show>
  );

  const row = <JobSegmentActionsRow segment={props.segment} renderActions={renderActions} />;

  return (
    <>
      <Show when={hasRow}>
        <Box paddingTop={4} paddingHorizontal={4}>
          {row}
        </Box>
      </Show>
      <Show when={props.isGridVisible}>
        <Show when={hasRow}>
          <Space vertical={4} />
          <Separator spacing={0} />
        </Show>
        <JobGridBand>
          <JobItemsDataGrid
            maximization={props.maximization}
            maximizedSectionHeader={<Show when={hasRow}>{row}</Show>}
            jobId={props.jobId}
            orderId={props.orderId}
            serviceCaseId={props.serviceCaseId}
            datagridKey={addLabourJobId ?? addMaterialRequestJobId}
            requestName={props.jobName}
            authorizationProfileId={props.authorizationProfileId}
            onGridDataChange={setGridData}
            data-testid={suffixTestId('items', props)}
          />
        </JobGridBand>
      </Show>
    </>
  );
}
